
import React from 'react'
import { X, MapPin, Calendar, Clock, FileText } from 'lucide-react'

interface HistoricoAlocacoesProps {
  isOpen: boolean
  onClose: () => void
  funcionario: any
  alocacoes: any[]
}

export const HistoricoAlocacoes: React.FC<HistoricoAlocacoesProps> = ({
  isOpen,
  onClose,
  funcionario,
  alocacoes
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'obra': return 'bg-blue-100 text-blue-800'
      case 'projeto': return 'bg-green-100 text-green-800'
      case 'manutencao': return 'bg-yellow-100 text-yellow-800'
      case 'consultoria': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'obra': return 'Obra'
      case 'projeto': return 'Projeto'
      case 'manutencao': return 'Manutenção'
      case 'consultoria': return 'Consultoria'
      default: return tipo
    }
  }

  const calculateDuration = (inicio: string, fim?: string) => {
    const dataInicio = new Date(inicio)
    const dataFim = fim ? new Date(fim) : new Date()
    const diffTime = Math.abs(dataFim.getTime() - dataInicio.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  if (!isOpen || !funcionario) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Histórico de Alocações</h2>
              <p className="text-gray-600">{funcionario.nome} - {funcionario.cargo}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {alocacoes.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma alocação encontrada</h3>
              <p className="mt-1 text-sm text-gray-500">
                Este funcionário ainda não foi alocado em nenhuma obra ou projeto.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {alocacoes.map((alocacao) => (
                <div key={alocacao._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{alocacao.nomeObra}</h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoColor(alocacao.tipo)}`}>
                          {getTipoLabel(alocacao.tipo)}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{alocacao.descricaoAtividade}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      <span>Início: {formatDate(alocacao.dataInicio)}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar size={16} className="mr-2" />
                      <span>
                        {alocacao.dataFim 
                          ? `Fim: ${formatDate(alocacao.dataFim)}`
                          : 'Em andamento'
                        }
                      </span>
                    </div>

                    <div className="flex items-center text-sm text-gray-600">
                      <Clock size={16} className="mr-2" />
                      <span>
                        Duração: {calculateDuration(alocacao.dataInicio, alocacao.dataFim)} dias
                      </span>
                    </div>
                  </div>

                  {alocacao.observacoes && (
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex items-start">
                        <FileText size={16} className="mr-2 mt-0.5 text-gray-400" />
                        <p className="text-sm text-gray-700">{alocacao.observacoes}</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>Registrado em: {formatDate(alocacao.createdAt)}</span>
                      {alocacao.updatedAt !== alocacao.createdAt && (
                        <span>Atualizado em: {formatDate(alocacao.updatedAt)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoricoAlocacoes
